/**
 * InflowColumn - Renders the left column of income source nodes
 *
 * Maps positioned inflow nodes to InflowNode components.
 */

import { memo, useCallback } from 'react';
import type { InflowNode as InflowNodeType, PositionedNode, FlowNode } from './types';
import { isInflowNode } from './types';
import { InflowNode } from './nodes';

export interface InflowColumnProps {
  positionedNodes: PositionedNode[];
  onNodeClick?: (node: FlowNode) => void;
  /** Called when mouse enters a node */
  onNodeHover?: (node: FlowNode | null, event?: React.MouseEvent) => void;
  /** ID of the currently highlighted node (if any) */
  highlightedNodeId?: string | null;
}

function InflowColumnComponent({
  positionedNodes,
  onNodeClick,
  onNodeHover,
  highlightedNodeId,
}: InflowColumnProps) {
  // Filter to only inflow nodes
  const inflowNodes = positionedNodes.filter(
    (p): p is PositionedNode & { node: InflowNodeType } => isInflowNode(p.node)
  );

  const handleMouseEnter = useCallback(
    (node: InflowNodeType, event: React.MouseEvent) => {
      onNodeHover?.(node, event);
    },
    [onNodeHover]
  );

  const handleMouseLeave = useCallback(() => {
    onNodeHover?.(null);
  }, [onNodeHover]);

  if (inflowNodes.length === 0) {
    return null;
  }

  return (
    <g className="inflow-column">
      {inflowNodes.map((positioned) => (
        <InflowNode
          key={positioned.node.id}
          node={positioned.node}
          x={positioned.x}
          y={positioned.y}
          width={positioned.width}
          height={positioned.height}
          onClick={onNodeClick as ((node: InflowNodeType) => void) | undefined}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          isHighlighted={highlightedNodeId === positioned.node.id}
        />
      ))}
    </g>
  );
}

export const InflowColumn = memo(InflowColumnComponent);
export default InflowColumn;
